import { toast as sonnerToast } from 'sonner';
import { ToastChannel, ToastOptions } from '@/types/toast';

let toastSettings: Record<string, boolean> = {};

export const setToastSettings = (settings: Record<string, boolean>) => {
    toastSettings = settings;
};

const shouldShow = (channel?: ToastChannel, id?: string | number) => {
    // 1. Specific message ID check (most granular)
    if (id && toastSettings[id.toString()] === false) return false;

    // 2. Channel-level switch
    if (channel && toastSettings[channel] === false) return false;

    return true;
};

interface ToastFunction {
    (message: string, options?: ToastOptions): string | number | undefined;
    success: (message: string, options?: ToastOptions) => string | number | undefined;
    error: (message: string, options?: ToastOptions) => string | number | undefined;
    info: (message: string, options?: ToastOptions) => string | number | undefined;
    warning: (message: string, options?: ToastOptions) => string | number | undefined;
    promise: <T>(
        promise: Promise<T> | (() => Promise<T>),
        data: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: any) => string);
        },
        options?: ToastOptions
    ) => any;
    dismiss: (id?: string | number) => void;
    custom: (jsx: ((id: string | number) => React.ReactNode) | React.ReactNode, options?: ToastOptions & { id?: string | number; duration?: number; position?: any }) => string | number | undefined;
}

export const toast: ToastFunction = (message: string, options?: ToastOptions) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast(message, options as any);
    }
    return undefined;
};

toast.success = (message: string, options?: ToastOptions) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.success(message, options as any);
    }
    return undefined;
};

toast.error = (message: string, options?: ToastOptions) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.error(message, options as any);
    }
    return undefined;
};

toast.info = (message: string, options?: ToastOptions) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.info(message, options as any);
    }
    return undefined;
};

toast.warning = (message: string, options?: ToastOptions) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.warning(message, options as any);
    }
    return undefined;
};

toast.promise = <T>(
    promise: Promise<T> | (() => Promise<T>),
    data: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: any) => string);
    },
    options?: ToastOptions
) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.promise(promise, {
            loading: data.loading,
            success: data.success as any,
            error: data.error as any,
            ...options
        });
    }

    if (typeof promise === 'function') {
        promise();
    }
    return undefined;
};

toast.dismiss = (id?: string | number) => {
    sonnerToast.dismiss(id);
};

toast.custom = (jsx: any, options?: any) => {
    if (shouldShow(options?.channel, options?.id)) {
        return sonnerToast.custom(jsx, options as any);
    }
    return undefined;
};
